import type { AgentComponentBody, AgentComponentType } from '@reach/shared-types/agent-components';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../hooks/useToast';
import { componentPath } from '../agent-component-paths';
import {
  agentComponentKeys,
  createAgentComponent,
  deleteAgentComponent,
  updateAgentComponent,
} from '../agent-components-api';
import { templateComponentKeys, unpublishedChangesKeys } from '../agent-template-api';
import { SAVE_COPY } from './component-page-copy';

const refreshComponentSummaries = (queryClient: QueryClient) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: agentComponentKeys.lists() }),
    queryClient.invalidateQueries({ queryKey: agentComponentKeys.toolOptions() }),
    queryClient.invalidateQueries({ queryKey: templateComponentKeys.all }),
  ]);

/** Creates or updates a component, refreshes the lists, and opens a newly created component. */
export function useSaveComponent(
  type: AgentComponentType,
  componentId: string | undefined,
  allowNavigationTo: (path: string) => void,
) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const componentIsNew = !componentId;

  return useMutation({
    mutationFn: (body: AgentComponentBody) =>
      componentId ? updateAgentComponent(componentId, body) : createAgentComponent(body),
    onSuccess: async savedComponent => {
      queryClient.setQueryData(agentComponentKeys.detail(savedComponent.id), savedComponent);
      await Promise.all([
        refreshComponentSummaries(queryClient),
        queryClient.invalidateQueries({ queryKey: unpublishedChangesKeys.all }),
      ]);
      addToast({ title: SAVE_COPY.saved, description: SAVE_COPY.savedDetail, variant: 'success' });
      if (componentIsNew) {
        const savedComponentPath = componentPath(type, savedComponent.id);
        allowNavigationTo(savedComponentPath);
        navigate(savedComponentPath);
      }
    },
    onError: (error: Error) => {
      addToast({ title: SAVE_COPY.saveFailed, description: error.message, variant: 'destructive' });
    },
  });
}

/** Deletes a component and refreshes the lists. */
export function useDeleteComponent(onDeleted?: () => void) {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (componentId: string) => deleteAgentComponent(componentId),
    onSuccess: async (_, componentId) => {
      onDeleted?.();
      addToast({ title: SAVE_COPY.deleted, variant: 'success' });
      queryClient.setQueryData(agentComponentKeys.detail(componentId), null);
      await refreshComponentSummaries(queryClient);
    },
    onError: (error: Error) => {
      addToast({ title: SAVE_COPY.deleteFailed, description: error.message, variant: 'destructive' });
    },
  });
}
